import constructionImg from "../../tools/industryimages/con3.webp";
import retailImg from "../../tools/industryimages/retail1.webp";
import manufactureImg from "../../tools/industryimages/manu1.webp";
import restaurantImg from "../../tools/industryimages/rest3.webp";
import securityImg from "../../tools/industryimages/security1.webp";
import healthcareImg from "../../tools/industryimages/health1.webp";
import officeImg from "../../tools/industryimages/office1.webp";
import cleaningImg from "../../tools/industryimages/cleaning1.webp";

export const industryData = {
  construction: {
    eyebrow: "Construction",
    title: "Workforce management built for construction teams",
    description:
      "Construction teams move across job sites, crews, shifts, and deadlines. Oiiko helps you manage schedules, track attendance, assign tasks, and keep labor visibility connected in one place.",
    image: constructionImg,
    stats: [
      { value: "25+", label: "Active job sites" },
      { value: "1,200+", label: "Hours tracked" },
      { value: "98%", label: "Verified clock-ins" },
      { value: "30%", label: "More labor visibility" },
    ],
    problems: [
      "Crews are spread across multiple job sites",
      "Managers struggle to verify where employees clock in",
      "Overtime and labor costs can get out of control quickly",
      "Tasks and job-site updates are often handled through texts or calls",
    ],
    features: [
      {
        title: "Location-Based Time Tracking",
        description:
          "See where employees clock in from so managers can verify job-site attendance.",
      },
      {
        title: "Multi-Location Scheduling",
        description:
          "Schedule crews across multiple job sites, projects, and shifts without confusion.",
      },
      {
        title: "Task Manager",
        description:
          "Assign job-site tasks with checklists and review completed work before approval.",
      },
      {
        title: "Payroll Reports",
        description:
          "Use accurate time and attendance data to prepare cleaner payroll reports.",
      },
      {
        title: "Analytics",
        description:
          "Track attendance, labor trends, employee performance, and operational issues before they grow.",
      },
    ],
  },

  restaurants: {
    eyebrow: "Restaurants",
    title: "Workforce management for restaurants",
    description:
      "Restaurant operations move fast. Oiiko helps restaurants manage shifts, attendance, time off, tasks, and labor visibility without relying on scattered tools.",
    image: restaurantImg,
    stats: [
      { value: "98%", label: "Shift visibility" },
      { value: "40+", label: "Weekly shifts managed" },
      { value: "3x", label: "Faster schedule updates" },
      { value: "30%", label: "Less payroll confusion" },
    ],
    problems: [
      "Last-minute callouts disrupt shift coverage",
      "Managers struggle with late arrivals and missed shifts",
      "Labor costs rise when overtime is not visible",
      "Opening, closing, and cleaning tasks are easy to miss",
    ],
    features: [
      {
        title: "Scheduling",
        description:
          "Build schedules around peak hours, roles, and staffing needs.",
      },
      {
        title: "Time Tracking",
        description:
          "Track clock-ins, late arrivals, missed shifts, and overtime in real time.",
      },
      {
        title: "Leave Management",
        description:
          "Manage time-off requests without creating gaps in shift coverage.",
      },
      {
        title: "Task Manager",
        description:
          "Assign opening, closing, cleaning, and prep tasks with clear accountability.",
      },
      {
        title: "Payroll Reports",
        description:
          "Prepare payroll using accurate shift, attendance, and hour data.",
      },
    ],
  },

  retail: {
    eyebrow: "Retail",
    title: "Workforce management for retail teams",
    description:
      "Retail teams need clear staffing, strong shift coverage, and better visibility during busy hours. Oiiko helps stores manage people, schedules, tasks, and attendance from one platform.",
    image: retailImg,
    stats: [
      { value: "24/7", label: "Store coverage" },
      { value: "95%", label: "Attendance visibility" },
      { value: "500+", label: "Tasks completed" },
      { value: "28%", label: "Operational efficiency" },
    ],
    problems: [
      "Peak-hour coverage is hard to plan",
      "Employees may be late or absent without quick visibility",
      "Store tasks are often handled informally",
      "Managers lack clear employee and store performance insights",
    ],
    features: [
      {
        title: "Scheduling",
        description:
          "Plan store schedules around peak hours, departments, and employee availability.",
      },
      {
        title: "Time Tracking",
        description:
          "Monitor attendance, late arrivals, and worked hours across your team.",
      },
      {
        title: "Task Manager",
        description:
          "Assign restocking, cleaning, opening, closing, and store operation tasks.",
      },
      {
        title: "Leave Management",
        description:
          "Manage employee time-off requests while protecting store coverage.",
      },
      {
        title: "Analytics",
        description:
          "View attendance trends, employee performance, and operational patterns.",
      },
    ],
  },

  security: {
    eyebrow: "Security",
    title: "Workforce management for security teams",
    description:
      "Security companies need reliable coverage, clear guard schedules, and strong attendance visibility. Oiiko helps manage posts, shifts, tasks, and workforce accountability.",
    image: securityImg,
    stats: [
      { value: "24/7", label: "Post coverage" },
      { value: "98%", label: "Attendance visibility" },
      { value: "25+", label: "Active sites" },
      { value: "30%", label: "Fewer coverage gaps" },
    ],
    problems: [
      "Guards work across different posts and locations",
      "Missed shifts can create serious coverage gaps",
      "Managers need better visibility into attendance",
      "Daily responsibilities and reports can become scattered",
    ],
    features: [
      {
        title: "Multi-Location Scheduling",
        description:
          "Assign guards to posts, sites, and shifts with better visibility.",
      },
      {
        title: "Location-Based Time Tracking",
        description:
          "Verify where guards clock in from and track attendance by location.",
      },
      {
        title: "Task Manager",
        description:
          "Assign patrol duties, checklists, and responsibilities for each post.",
      },
      {
        title: "Analytics",
        description:
          "Track attendance patterns, late arrivals, and coverage issues.",
      },
      {
        title: "Payroll Reports",
        description:
          "Use accurate shift and time data to support payroll preparation.",
      },
    ],
  },

  healthcare: {
    eyebrow: "Healthcare",
    title: "Workforce management for healthcare teams",
    description:
      "Healthcare teams depend on reliable staffing, scheduling, training, and compliance. Oiiko helps manage daily workforce operations with better structure and visibility.",
    image: healthcareImg,
    stats: [
      { value: "99%", label: "Coverage visibility" },
      { value: "120+", label: "Shifts managed" },
      { value: "85%", label: "Training completion" },
      { value: "35%", label: "Scheduling efficiency" },
    ],
    problems: [
      "Rotating shifts can be difficult to manage",
      "Staffing gaps affect patient care operations",
      "Training and compliance tasks need clear tracking",
      "Leave requests must be handled carefully to protect coverage",
    ],
    features: [
      {
        title: "Scheduling",
        description:
          "Manage rotating shifts, departments, and staff coverage with more clarity.",
      },
      {
        title: "Time Tracking",
        description:
          "Track clock-ins, attendance, lateness, and completed work hours.",
      },
      {
        title: "Leave Management",
        description:
          "Handle sick time, PTO, VTO, and other time-off requests in one place.",
      },
      {
        title: "Training Manager",
        description:
          "Assign employee training, compliance courses, and deadlines.",
      },
      {
        title: "Analytics",
        description:
          "Monitor attendance, staffing trends, and individual employee performance.",
      },
    ],
  },

  cleaning: {
    eyebrow: "Cleaning Services",
    title: "Workforce management for cleaning teams",
    description:
      "Cleaning and janitorial teams often work across multiple client sites. Oiiko helps manage crew schedules, location-based attendance, tasks, and service accountability.",
    image: cleaningImg,
    stats: [
      { value: "25+", label: "Client locations" },
      { value: "98%", label: "Verified arrivals" },
      { value: "1,200+", label: "Tasks completed" },
      { value: "30%", label: "More service visibility" },
    ],
    problems: [
      "Crews work across different client locations",
      "Managers need proof that employees arrived on-site",
      "Cleaning tasks need clear checklists and approval",
      "Missed jobs or late arrivals can hurt service quality",
    ],
    features: [
      {
        title: "Multi-Location Scheduling",
        description:
          "Schedule cleaning crews across client sites, shifts, and service windows.",
      },
      {
        title: "Location-Based Time Tracking",
        description:
          "See where employees clock in from when they arrive at a client location.",
      },
      {
        title: "Task Manager",
        description:
          "Assign cleaning checklists and review completed work before approval.",
      },
      {
        title: "Payroll Reports",
        description:
          "Generate reports from accurate time, attendance, and shift records.",
      },
      {
        title: "Analytics",
        description:
          "Track crew attendance, job completion, and employee performance trends.",
      },
    ],
  },

  corporate: {
    eyebrow: "Corporate Teams",
    title: "Workforce management for corporate teams",
    description:
      "Corporate teams need organized employee operations, clear task ownership, training, time-off visibility, and internal support. Oiiko brings those workflows together.",
    image: officeImg,
    stats: [
      { value: "1,000+", label: "Tasks completed" },
      { value: "90%", label: "Workflow visibility" },
      { value: "250+", label: "Tickets resolved" },
      { value: "40%", label: "Faster approvals" },
    ],
    problems: [
      "Employee requests are scattered across emails and messages",
      "Tasks lack clear ownership and approval",
      "Training progress can be hard to monitor",
      "Internal support requests need better organization",
    ],
    features: [
      {
        title: "Task Manager",
        description:
          "Assign work, add checklists, review submissions, and approve completed tasks.",
      },
      {
        title: "Leave Management",
        description:
          "Manage PTO, sick time, VTO, and other employee time-off requests.",
      },
      {
        title: "Expense Management",
        description:
          "Review and manage employee expense submissions in one place.",
      },
      {
        title: "Training Manager",
        description:
          "Assign internal training and track employee completion progress.",
      },
      {
        title: "IT Help Desk",
        description:
          "Give employees a clear place to submit support tickets and track issue resolution.",
      },
      {
        title: "Analytics",
        description:
          "View company-wide and individual employee analytics for better decisions.",
      },
    ],
  },

  manufacturing: {
    eyebrow: "Manufacturing",
    title: "Workforce management for manufacturing teams",
    description:
      "Manufacturing teams need reliable shift coverage, attendance visibility, training, and workforce performance insights. Oiiko helps keep production teams organized.",
    image: manufactureImg,
    stats: [
      { value: "98%", label: "Shift coverage" },
      { value: "12k+", label: "Hours tracked" },
      { value: "500+", label: "Tasks completed" },
      { value: "32%", label: "Operational visibility" },
    ],
    problems: [
      "Production shifts require reliable coverage",
      "Late arrivals can disrupt operations",
      "Overtime needs to be tracked clearly",
      "Training and task completion need better visibility",
    ],
    features: [
      {
        title: "Scheduling",
        description:
          "Plan production shifts based on departments, roles, and coverage needs.",
      },
      {
        title: "Time Tracking",
        description:
          "Track clock-ins, attendance, late arrivals, and overtime.",
      },
      {
        title: "Training Manager",
        description:
          "Assign safety, compliance, and role-based training to employees.",
      },
      {
        title: "Task Manager",
        description:
          "Assign operational tasks with checklists and approval workflows.",
      },
      {
        title: "Payroll Reports",
        description:
          "Prepare payroll reports using accurate shift and attendance data.",
      },
      {
        title: "Analytics",
        description:
          "Monitor workforce efficiency, attendance trends, and employee performance.",
      },
    ],
  },
};
